import { SoraneApi, type MessageRow, type ThreadRow } from './api.ts';
import { threadTitle } from './thread-title.ts';
import type { SoraneConfig } from './api.ts';

type View = 'main' | 'thread';
type Tab = 'new' | 'list';
type SessionRole = 'guest' | 'inquirer' | 'admin';

function appendNodes(parent: Node, ...children: (Node | string)[]): void {
  for (const child of children) {
    parent.appendChild(child instanceof Node ? child : document.createTextNode(child));
  }
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  children: (Node | string)[] = [],
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  appendNodes(node, ...children);
  return node;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function roleLabel(msg: MessageRow): string {
  if (msg.authorRole === 'agent') {
    if (msg.agentName && msg.model) return `${msg.agentName}の${msg.model}`;
    if (msg.model) return msg.model;
    return 'AI';
  }
  if (msg.authorRole === 'admin') return '管理者';
  return msg.inquirerDisplayName ? `${msg.inquirerDisplayName}からの質問` : '質問';
}

function safeHttpUrl(raw: string): string | null {
  try {
    const url = new URL(raw);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    return url.toString();
  } catch {
    return null;
  }
}

function appendInlineMarkdown(parent: HTMLElement, text: string): void {
  const inlineRe =
    /\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)|(https?:\/\/[^\s<>()]+[^\s<>().,、。])|\*\*([^*\n]+)\*\*|\*([^*\n]+)\*/g;
  let last = 0;
  for (const match of text.matchAll(inlineRe)) {
    if (match.index === undefined) continue;
    if (match.index > last) {
      parent.appendChild(document.createTextNode(text.slice(last, match.index)));
    }
    const markdownLinkLabel = match[1];
    const markdownLinkHref = match[2];
    const bareUrl = match[3];
    const strongText = match[4];
    const emphasisText = match[5];
    if (markdownLinkHref || bareUrl) {
      const label = markdownLinkLabel ?? bareUrl ?? '';
      const href = safeHttpUrl(markdownLinkHref ?? bareUrl ?? '');
      if (!href) {
        parent.appendChild(document.createTextNode(match[0]));
        last = match.index + match[0].length;
        continue;
      }
      const a = el('a', '', [label]);
      a.href = href;
      a.rel = 'noopener noreferrer';
      a.target = '_blank';
      parent.appendChild(a);
    } else if (strongText) {
      parent.appendChild(el('strong', '', [strongText]));
    } else if (emphasisText) {
      parent.appendChild(el('em', '', [emphasisText]));
    } else {
      parent.appendChild(document.createTextNode(match[0]));
    }
    last = match.index + match[0].length;
  }
  if (last < text.length) {
    parent.appendChild(document.createTextNode(text.slice(last)));
  }
}

type TableAlign = 'left' | 'center' | 'right' | null;

function splitMarkdownTableRow(line: string): string[] {
  const cells: string[] = [];
  let cell = '';
  let escaped = false;
  for (const char of line.trim()) {
    if (escaped) {
      cell += char;
      escaped = false;
      continue;
    }
    if (char === '\\') {
      escaped = true;
      continue;
    }
    if (char === '|') {
      cells.push(cell.trim());
      cell = '';
      continue;
    }
    cell += char;
  }
  if (escaped) cell += '\\';
  cells.push(cell.trim());
  if (cells[0] === '') cells.shift();
  if (cells.at(-1) === '') cells.pop();
  return cells;
}

function parseTableAlignments(line: string, columnCount: number): TableAlign[] | null {
  const cells = splitMarkdownTableRow(line);
  if (cells.length !== columnCount) return null;
  const aligns: TableAlign[] = [];
  for (const cell of cells) {
    const normalized = cell.replace(/\s+/g, '');
    if (!/^:?-{3,}:?$/.test(normalized)) return null;
    const left = normalized.startsWith(':');
    const right = normalized.endsWith(':');
    aligns.push(left && right ? 'center' : right ? 'right' : left ? 'left' : null);
  }
  return aligns;
}

function renderMarkdownTable(headerLine: string, separatorLine: string, rowLines: string[]): HTMLElement | null {
  const headers = splitMarkdownTableRow(headerLine);
  if (headers.length === 0) return null;
  const aligns = parseTableAlignments(separatorLine, headers.length);
  if (!aligns) return null;

  const wrap = el('div', 'sorane__table-wrap');
  const table = el('table', 'sorane__markdown-table');
  const thead = el('thead');
  const headerRow = el('tr');
  headers.forEach((header, index) => {
    const th = el('th');
    if (aligns[index]) th.style.textAlign = aligns[index]!;
    appendInlineMarkdown(th, header);
    headerRow.appendChild(th);
  });
  thead.appendChild(headerRow);
  table.appendChild(thead);

  const tbody = el('tbody');
  for (const rowLine of rowLines) {
    const cells = splitMarkdownTableRow(rowLine);
    const tr = el('tr');
    for (let i = 0; i < headers.length; i += 1) {
      const td = el('td');
      if (aligns[i]) td.style.textAlign = aligns[i]!;
      appendInlineMarkdown(td, cells[i] ?? '');
      tr.appendChild(td);
    }
    tbody.appendChild(tr);
  }
  table.appendChild(tbody);
  wrap.appendChild(table);
  return wrap;
}

function renderMarkdownLite(markdown: string): HTMLElement {
  const root = el('div', 'sorane__message-body');
  const lines = markdown.replace(/\r\n?/g, '\n').split('\n');
  let paragraph: string[] = [];
  let list: HTMLElement | null = null;

  const flushParagraph = () => {
    if (paragraph.length === 0) return;
    const p = el('p');
    appendInlineMarkdown(p, paragraph.join('\n'));
    root.appendChild(p);
    paragraph = [];
  };
  const flushList = () => {
    list = null;
  };

  for (let i = 0; i < lines.length; i += 1) {
    const rawLine = lines[i]!;
    const line = rawLine.trimEnd();
    if (line.trim() === '') {
      flushParagraph();
      flushList();
      continue;
    }

    const nextLine = lines[i + 1]?.trimEnd();
    if (nextLine !== undefined && line.includes('|') && nextLine.includes('|')) {
      const headers = splitMarkdownTableRow(line);
      if (headers.length > 0 && parseTableAlignments(nextLine, headers.length)) {
        flushParagraph();
        flushList();
        const rowLines: string[] = [];
        let j = i + 2;
        while (j < lines.length) {
          const rowLine = lines[j]!.trimEnd();
          if (rowLine.trim() === '') break;
          if (!rowLine.includes('|')) break;
          const rowCells = splitMarkdownTableRow(rowLine);
          if (rowCells.length === 0 || rowCells.length > headers.length) break;
          rowLines.push(rowLine);
          j += 1;
        }
        const table = renderMarkdownTable(line, nextLine, rowLines);
        if (table) {
          root.appendChild(table);
          i = j - 1;
          continue;
        }
      }
    }

    const heading = line.match(/^(#{2,4})\s+(.+)$/);
    if (heading) {
      flushParagraph();
      flushList();
      const h = el(heading[1]!.length === 2 ? 'h3' : 'h4');
      appendInlineMarkdown(h, heading[2]!.trim());
      root.appendChild(h);
      continue;
    }

    const bullet = line.match(/^\s*[-*]\s+(.+)$/);
    if (bullet) {
      flushParagraph();
      if (!list || list.tagName !== 'UL') {
        list = el('ul');
        root.appendChild(list);
      }
      const li = el('li');
      appendInlineMarkdown(li, bullet[1]!.trim());
      list.appendChild(li);
      continue;
    }

    const numbered = line.match(/^\s*\d+\.\s+(.+)$/);
    if (numbered) {
      flushParagraph();
      if (!list || list.tagName !== 'OL') {
        list = el('ol');
        root.appendChild(list);
      }
      const li = el('li');
      appendInlineMarkdown(li, numbered[1]!.trim());
      list.appendChild(li);
      continue;
    }

    flushList();
    paragraph.push(line);
  }
  flushParagraph();
  return root;
}

function maxBirthDateForAdult(): string {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 18);
  return d.toISOString().slice(0, 10);
}

const DISPLAY_ALIAS_MIN = 3;
const DISPLAY_ALIAS_MAX = 16;
const DISPLAY_ALIAS_RE = /^[\p{L}\p{N}_-]+$/u;

function resolveDisplayAlias(raw: string, fallback: string): string {
  const trimmed = raw.trim();
  return trimmed ? trimmed.normalize('NFKC') : fallback;
}

function validateDisplayAliasInput(raw: string): string | null {
  const alias = raw.trim().normalize('NFKC');
  if (!alias) return null;
  if (alias.length < DISPLAY_ALIAS_MIN) {
    return `表示名は${DISPLAY_ALIAS_MIN}文字以上で入力してください`;
  }
  if (alias.length > DISPLAY_ALIAS_MAX) {
    return `表示名は${DISPLAY_ALIAS_MAX}文字以内にしてください`;
  }
  if (!DISPLAY_ALIAS_RE.test(alias)) {
    return '表示名に使えるのは文字・数字・ハイフン・アンダースコアだけです';
  }
  return null;
}

export class SoraneApp {
  private readonly api: SoraneApi;
  private view: View = 'main';
  private tab: Tab = 'list';
  private sessionRole: SessionRole = 'guest';
  private displayName: string | null = null;
  private onboardingComplete = false;
  private showOnboarding = false;
  private memberConduct = '';
  private suggestedDisplayName: string | null = null;
  private threads: ThreadRow[] = [];
  private activeThread: ThreadRow | null = null;
  private messages: MessageRow[] = [];
  private busy = false;

  private get canPost(): boolean {
    return this.sessionRole === 'inquirer' && this.onboardingComplete;
  }

  private readonly root: HTMLElement;
  private readonly headerEl: HTMLElement | null;
  private readonly statusEl: HTMLElement;
  private readonly contentEl: HTMLElement;

  constructor(
    private readonly container: HTMLElement,
    private readonly config: SoraneConfig,
    headerMount?: HTMLElement | null,
  ) {
    this.api = new SoraneApi(config);
    this.headerEl = headerMount ?? document.getElementById('sorane-header-actions');
    this.root = el('div', 'sorane');
    this.statusEl = el('div', 'sorane__status');
    this.contentEl = el('main');
    appendNodes(this.root, this.contentEl, this.statusEl);
    this.container.replaceChildren(this.root);
  }

  async mount(): Promise<void> {
    await this.refreshSession();
    this.view = 'main';
    this.tab = this.showOnboarding ? 'new' : 'list';
    await this.loadThreads();
    this.renderHeader();
    if (!this.showOnboarding) {
      await this.openThreadFromQuery();
    }
  }

  private applyInquirerSession(session: {
    displayName?: string;
    displaySlug?: string;
    onboardingComplete?: boolean;
  }): void {
    this.sessionRole = 'inquirer';
    this.displayName = session.displayName ?? session.displaySlug ?? null;
    this.onboardingComplete = session.onboardingComplete === true;
    this.showOnboarding = !this.onboardingComplete;
  }

  private async refreshSession(): Promise<void> {
    const session = await this.api.getSession();
    if (session?.role === 'inquirer') {
      this.applyInquirerSession(session);
      return;
    }
    this.sessionRole = 'guest';
    this.displayName = null;
    this.onboardingComplete = false;
    this.showOnboarding = false;
  }

  private renderHeader(): void {
    if (!this.headerEl) return;
    this.headerEl.replaceChildren();
    this.headerEl.className = 'sorane-site__header-actions';

    if (this.sessionRole === 'inquirer') {
      if (this.displayName) {
        appendNodes(this.headerEl, el('span', 'sorane-site__member', [this.displayName]));
      } else if (this.showOnboarding) {
        appendNodes(this.headerEl, el('span', 'sorane-site__member', ['参加手続き中']));
      }
      const logoutBtn = el('button', 'sorane__btn sorane__btn--secondary sorane-site__join', [
        'ログアウト',
      ]);
      logoutBtn.type = 'button';
      logoutBtn.addEventListener('click', () => void this.handleLogout());
      appendNodes(this.headerEl, logoutBtn);
      return;
    }

    const joinBtn = el('button', 'sorane__btn sorane-site__join', ['参加']);
    joinBtn.type = 'button';
    joinBtn.addEventListener('click', () => void this.handleJoin());
    appendNodes(this.headerEl, joinBtn);
  }

  private async openThreadFromQuery(): Promise<void> {
    const url = new URL(window.location.href);
    const ref = url.searchParams.get('ref')?.trim();
    const threadId = url.searchParams.get('thread')?.trim();
    const lookup = ref ?? threadId;
    if (!lookup) return;
    await this.openThread(lookup);
  }

  private setThreadQuery(thread: ThreadRow): void {
    const url = new URL(window.location.href);
    url.searchParams.set('ref', thread.ref);
    url.searchParams.delete('thread');
    window.history.replaceState(null, '', url.pathname + url.search + url.hash);
  }

  private clearThreadQuery(): void {
    const url = new URL(window.location.href);
    url.searchParams.delete('ref');
    url.searchParams.delete('thread');
    window.history.replaceState(null, '', url.pathname + url.search + url.hash);
  }

  private async render(): Promise<void> {
    this.contentEl.replaceChildren();
    this.setStatus('');

    if (this.showOnboarding) {
      appendNodes(this.contentEl, await this.renderOnboarding());
      return;
    }

    if (this.view === 'thread' && this.activeThread) {
      appendNodes(this.contentEl, this.renderThread());
      return;
    }
    appendNodes(this.contentEl, this.renderMain());
  }

  private async ensureOnboardingData(): Promise<void> {
    if (!this.memberConduct) {
      this.memberConduct = await this.api.getMemberConduct();
    }
    if (!this.suggestedDisplayName) {
      this.suggestedDisplayName = await this.api.suggestPresetDisplayName();
    }
  }

  private async renderOnboarding(): Promise<HTMLElement> {
    await this.ensureOnboardingData();

    const panel = el('div', 'sorane__hero sorane__stack sorane__onboarding');
    appendNodes(panel, el('h2', 'sorane__title', ['参加の確認']));

    const form = el('form', 'sorane__stack');

    const birthLabel = el('label', 'sorane__stack');
    appendNodes(
      birthLabel,
      el('span', 'sorane__label', ['生年月日', el('span', 'sorane__required', ['*'])]),
      el('span', 'sorane__meta', ['成年確認のため。公開されません。']),
    );
    const birthInput = el('input', 'sorane__input') as HTMLInputElement;
    birthInput.type = 'date';
    birthInput.required = true;
    birthInput.max = maxBirthDateForAdult();
    appendNodes(birthLabel, birthInput);

    const presetName = this.suggestedDisplayName!;
    const nameLabel = el('label', 'sorane__stack');
    appendNodes(
      nameLabel,
      el('span', 'sorane__label', ['表示名', el('span', 'sorane__required', ['*'])]),
      el(
        'span',
        'sorane__meta',
        [
          `空欄のときは仮IDを使います。自分で決める場合は${DISPLAY_ALIAS_MIN}〜${DISPLAY_ALIAS_MAX}文字。`,
        ],
      ),
    );
    const presetHint = el('span', 'sorane__preset-id', [`仮ID: ${presetName}`]);
    const nameRow = el('div', 'sorane__row');
    const nameInput = el('input', 'sorane__input sorane__display-name-input') as HTMLInputElement;
    nameInput.type = 'text';
    nameInput.maxLength = DISPLAY_ALIAS_MAX;
    nameInput.autocomplete = 'off';
    nameInput.placeholder = '自分で決める';
    const nameRefresh = el('button', 'sorane__btn sorane__btn--secondary', ['別の仮名']);
    nameRefresh.type = 'button';
    nameRefresh.addEventListener('click', () => {
      void (async () => {
        this.suggestedDisplayName = await this.api.suggestPresetDisplayName();
        presetHint.textContent = `仮ID: ${this.suggestedDisplayName}`;
      })();
    });
    appendNodes(nameRow, nameInput, nameRefresh);
    appendNodes(nameLabel, presetHint, nameRow);

    const conductBox = el('div', 'sorane__conduct');
    appendNodes(conductBox, el('pre', 'sorane__conduct-text', [this.memberConduct]));

    const agreeLabel = el('label', 'sorane__check');
    const agreeInput = el('input') as HTMLInputElement;
    agreeInput.type = 'checkbox';
    agreeInput.required = true;
    appendNodes(agreeLabel, agreeInput, document.createTextNode(' 行動規範に同意する'));
    appendNodes(conductBox, agreeLabel);

    const submit = el('button', 'sorane__btn', ['参加する']);
    submit.type = 'submit';
    appendNodes(form, birthLabel, nameLabel, conductBox, submit);
    form.addEventListener('submit', (ev) => {
      ev.preventDefault();
      void this.handleCompleteOnboarding(birthInput.value, nameInput.value, agreeInput.checked);
    });
    appendNodes(panel, form);
    return panel;
  }

  private renderMain(): HTMLElement {
    const wrap = el('div', 'sorane__stack');

    const tabs = el('div', 'sorane__tabs');
    tabs.setAttribute('role', 'tablist');
    const newTab = el(
      'button',
      `sorane__tab${this.tab === 'new' ? ' sorane__tab--active' : ''}`,
      ['新しい問い'],
    );
    newTab.type = 'button';
    newTab.setAttribute('role', 'tab');
    newTab.setAttribute('aria-selected', this.tab === 'new' ? 'true' : 'false');
    newTab.addEventListener('click', () => {
      this.tab = 'new';
      if (this.sessionRole === 'inquirer' && !this.onboardingComplete) {
        this.showOnboarding = true;
      }
      void this.render();
    });
    const listTab = el(
      'button',
      `sorane__tab${this.tab === 'list' ? ' sorane__tab--active' : ''}`,
      ['一覧'],
    );
    listTab.type = 'button';
    listTab.setAttribute('role', 'tab');
    listTab.setAttribute('aria-selected', this.tab === 'list' ? 'true' : 'false');
    listTab.addEventListener('click', () => {
      this.tab = 'list';
      void this.loadThreads();
    });
    appendNodes(tabs, newTab, listTab);
    appendNodes(wrap, tabs);

    if (this.tab === 'new') {
      if (this.canPost) {
        appendNodes(wrap, this.renderSubmitForm());
      } else if (this.sessionRole === 'inquirer' && !this.onboardingComplete) {
        appendNodes(
          wrap,
          el('p', 'sorane__note', ['参加の確認が未完了です。上のフォームから手続きを完了してください。']),
        );
      } else {
        appendNodes(wrap, this.renderPostPrompt());
      }
    } else {
      appendNodes(wrap, this.renderThreadList());
    }

    return wrap;
  }

  private renderPostPrompt(): HTMLElement {
    const panel = el('div', 'sorane__panel sorane__stack');
    appendNodes(
      panel,
      el('p', 'sorane__note', [
        '問いを投稿するには、右上の「参加」を押してください。',
      ]),
    );
    return panel;
  }

  private renderSubmitForm(): HTMLElement {
    const panel = el('div', 'sorane__panel sorane__stack');
    const form = el('form', 'sorane__stack');

    const qLabel = el('label', 'sorane__stack');
    appendNodes(
      qLabel,
      el('span', 'sorane__label', ['問い', el('span', 'sorane__required', ['*'])]),
    );
    const qInput = el('textarea', 'sorane__textarea') as HTMLTextAreaElement;
    qInput.placeholder = '知りたいこと、議論したいことを500文字以内で書いてください。';
    qInput.required = true;
    qInput.maxLength = 500;
    const counter = el('div', 'sorane__counter sorane__meta', ['残り 500 文字']);
    qInput.addEventListener('input', () => {
      const remaining = 500 - qInput.value.length;
      counter.textContent = `残り ${remaining} 文字`;
    });
    appendNodes(qLabel, qInput, counter);

    const submit = el('button', 'sorane__btn', ['投稿する']);
    submit.type = 'submit';
    appendNodes(form, qLabel, submit);
    form.addEventListener('submit', (ev) => {
      ev.preventDefault();
      void this.handleSubmit(qInput.value);
    });
    appendNodes(panel, form);
    return panel;
  }

  private renderThreadList(): HTMLElement {
    const panel = el('div', 'sorane__panel');
    if (this.threads.length === 0) {
      appendNodes(panel, el('p', 'sorane__meta', ['まだ問いはありません。']));
      return panel;
    }

    const list = el('ul', 'sorane__thread-list');
    for (const thread of this.threads) {
      const item = el('li', 'sorane__thread-item');
      const link = el('button', 'sorane__link', [threadTitle(thread)]);
      link.type = 'button';
      link.addEventListener('click', () => void this.openThread(thread.ref));
      const metaParts: (string | HTMLElement)[] = [];
      if (thread.status === 'pending') {
        metaParts.push(el('span', 'sorane__badge', ['非公開']));
        metaParts.push(' · ');
      }
      metaParts.push(formatDate(thread.updatedAt));
      appendNodes(item, link, el('div', 'sorane__meta', metaParts));
      appendNodes(list, item);
    }
    appendNodes(panel, list);
    return panel;
  }

  private renderThread(): HTMLElement {
    const thread = this.activeThread!;
    const wrap = el('div', 'sorane__stack');

    const back = el('button', 'sorane__btn sorane__btn--text', ['← 一覧に戻る']);
    back.addEventListener('click', () => {
      this.view = 'main';
      this.tab = 'list';
      this.activeThread = null;
      this.clearThreadQuery();
      void this.loadThreads();
    });
    appendNodes(wrap, back);



    if (thread.status === 'pending' && this.messages.length === 0 && thread.subject) {
      const pending = el('div', 'sorane__message');
      appendNodes(
        pending,
        el('div', 'sorane__meta', ['非公開']),
        el('div', '', [thread.subject]),
      );
      appendNodes(wrap, pending);
    } else if (thread.subject && this.messages.length === 0) {
      appendNodes(wrap, el('p', '', [thread.subject]));
    }

    const messagesEl = el('div', 'sorane__messages');
    for (const msg of this.messages) {
      const cls =
        msg.authorRole === 'agent'
          ? 'sorane__message sorane__message--agent'
          : msg.authorRole === 'inquirer'
            ? 'sorane__message sorane__message--inquirer'
            : 'sorane__message';
      const box = el('div', cls);
      appendNodes(
        box,
        el('div', 'sorane__meta', [`${roleLabel(msg)} · ${formatDate(msg.createdAt)}`]),
        renderMarkdownLite(msg.body),
      );
      appendNodes(messagesEl, box);
    }
    appendNodes(wrap, messagesEl);

    if (this.messages.filter((m) => m.authorRole === 'agent').length === 0) {
      appendNodes(wrap, el('p', 'sorane__meta', ['AI の応答を待っています。']));
    }

    return wrap;
  }

  private async handleJoin(): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    this.setStatus('Passkey を確認しています…');
    try {
      const existing = await this.api.getSession();
      if (existing && existing.role !== 'inquirer') {
        await this.api.logout();
      }
      await this.api.continueWithPasskey();
      await this.refreshSession();
      if (this.sessionRole === 'inquirer' && !this.onboardingComplete) {
        this.showOnboarding = true;
        this.suggestedDisplayName = null;
        this.setStatus('');
        this.renderHeader();
        await this.render();
        return;
      }
      this.tab = 'new';
      this.setStatus('参加しました', 'ok');
      this.renderHeader();
      await this.render();
    } catch (e) {
      const cancelled = e instanceof Error && e.message === 'Passkey cancelled';
      this.setStatus(
        cancelled ? '' : e instanceof Error ? e.message : 'Passkey の確認に失敗しました',
        cancelled ? undefined : 'error',
      );
    } finally {
      this.busy = false;
    }
  }

  private async handleCompleteOnboarding(
    birthDate: string,
    displayNameInput: string,
    conductAccepted: boolean,
  ): Promise<void> {
    if (this.busy) return;
    const fallback = this.suggestedDisplayName;
    if (!fallback) {
      this.setStatus('仮IDを読み込めませんでした。もう一度お試しください。', 'error');
      return;
    }
    const displayName = resolveDisplayAlias(displayNameInput, fallback);
    const aliasError = validateDisplayAliasInput(displayName);
    if (aliasError) {
      this.setStatus(aliasError, 'error');
      return;
    }
    this.busy = true;
    this.setStatus('登録しています…');
    try {
      const session = await this.api.completeOnboarding({
        birthDate,
        displayName,
        conductAccepted,
      });
      this.applyInquirerSession(session);
      this.showOnboarding = false;
      this.tab = 'new';
      this.setStatus('参加しました', 'ok');
      this.renderHeader();
      await this.render();
    } catch (e) {
      this.setStatus(e instanceof Error ? e.message : '登録に失敗しました', 'error');
    } finally {
      this.busy = false;
    }
  }

  private async handleLogout(): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    try {
      await this.api.logout();
      this.sessionRole = 'guest';
      this.displayName = null;
      this.onboardingComplete = false;
      this.showOnboarding = false;
      this.activeThread = null;
      this.clearThreadQuery();
      this.view = 'main';
      this.tab = 'list';
      this.setStatus('ログアウトしました', 'ok');
      this.renderHeader();
      await this.loadThreads();
    } catch (e) {
      this.setStatus(e instanceof Error ? e.message : 'ログアウトに失敗しました', 'error');
    } finally {
      this.busy = false;
    }
  }

  private async handleSubmit(question: string): Promise<void> {
    if (!this.canPost) {
      this.setStatus('投稿するには参加（Passkey 登録）が必要です', 'error');
      return;
    }
    if (this.busy) return;
    this.busy = true;
    this.setStatus('投稿しています…');
    try {
      await this.api.createThread(question.trim());
      this.setStatus('投稿しました', 'ok');
      this.tab = 'list';
      await this.loadThreads();
    } catch (e) {
      this.setStatus(e instanceof Error ? e.message : '投稿に失敗しました', 'error');
    } finally {
      this.busy = false;
    }
  }

  private async loadThreads(): Promise<void> {
    this.view = 'main';
    this.activeThread = null;
    this.setStatus('読み込み中…');
    try {
      this.threads = await this.api.listThreads();
      this.setStatus('');
      await this.render();
    } catch (e) {
      this.setStatus(e instanceof Error ? e.message : '一覧の取得に失敗しました', 'error');
    }
  }

  private async openThread(lookup: string): Promise<void> {
    this.setStatus('読み込み中…');
    try {
      const res = await this.api.getThread(lookup);
      this.activeThread = res.thread;
      this.messages = res.messages;
      this.view = 'thread';
      this.setThreadQuery(res.thread);
      this.setStatus('');
      await this.render();
    } catch (e) {
      this.setStatus(e instanceof Error ? e.message : 'スレッドの取得に失敗しました', 'error');
    }
  }

  private setStatus(message: string, kind?: 'error' | 'ok'): void {
    this.statusEl.textContent = message;
    this.statusEl.className = 'sorane__status';
    if (kind === 'error') this.statusEl.classList.add('sorane__status--error');
    if (kind === 'ok') this.statusEl.classList.add('sorane__status--ok');
    if (!message) this.statusEl.className = 'sorane__status';
  }
}
